import { UserService } from '@/common/services/user/userServices';
import { useChatStore } from '@/features/chat/stores/chatStore';
import { UserRole } from '@/common/constants';
import { UserProfile, UserType, User } from '../types/userTypes';
import { USER_TYPE } from '../constants';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useUserStore = defineStore('user', () => {
  const isLoading = ref(false);
  const userName = ref('');
  const userEmail = ref('');
  const userRoles = ref<UserRole[]>([]);
  const user = ref<User | null>(null);
  const userProfile = ref<UserProfile | null>(null);
  const isAuthenticated = ref(false);
  const accessToken = ref('');
  const lastAction = ref('');
  const lastActionPayload = ref('');
  const account = ref(null);
  const userType = ref<UserType | null>(null);
  //TODO : locale from remote profile (already exists for student but not for employee)
  const culture = ref(localStorage.getItem('locale') || 'fr'); // Default from local storage

  function setUserName(name: string) {
    userName.value = name;
  }

  function setUserEmail(email: string) {
    userEmail.value = email;
  }

  /**
   * Définit les rôles Azure AD et en déduit le type utilisateur (USER_TYPE).
   * Mapping 1:1 :
   *   - UserRole.ADMINISTRATOR → USER_TYPE.ADMINISTRATOR
   *   - UserRole.COLLABORATEUR → USER_TYPE.COLLABORATEUR
   *   - UserRole.STUDENT       → USER_TYPE.STUDENT
   * Priorité : Administrator > Collaborateur > Student
   */
  function setUserRoles(roles: UserRole[]) {
    userRoles.value = roles;
    if (roles.includes(UserRole.ADMINISTRATOR)) {
      userType.value = USER_TYPE.ADMINISTRATOR;
    } else if (roles.includes(UserRole.COLLABORATEUR)) {
      userType.value = USER_TYPE.COLLABORATEUR;
    } else if (roles.includes(UserRole.STUDENT)) {
      userType.value = USER_TYPE.STUDENT;
    } else {
      userType.value = null;
    }
  }

  function setUser(userData: User | null) {
    user.value = userData;
  }

  function setUserProfile(profile: UserProfile | null) {
    userProfile.value = profile;
  }

  function setUserPhoto(photo: string) {
    if (user.value) {
      user.value = { ...user.value, photo };
    }
  }

  function setIsAuthenticated(isAuth: boolean) {
    isAuthenticated.value = isAuth;
  }

  function setAccessToken(token: string) {
    accessToken.value = token;
  }

  function setLastAction(action: string) {
    lastAction.value = action;
  }

  function setLastActionPayload(payload: string) {
    lastActionPayload.value = payload;
  }

  function setAccount(account: any) {
    account.value = account;
  }

  function setLoading(loading: boolean) {
    isLoading.value = loading;
  }

  function setCulture(newCulture: string) {
    culture.value = newCulture;
    localStorage.setItem('locale', newCulture);
  }

  async function getUser() {
    const currentUser = await UserService.getUser();
    if (currentUser) {
      const chatStore = useChatStore();
      const currentChatUser = {
        id: currentUser.id,
        mail: currentUser.mail,
        firstName: currentUser.firstname,
        lastName: currentUser.lastname,
        photo: currentUser.photo || null,
      };
      chatStore.setCurrentUser(currentChatUser);
      setUser(currentUser);
    }
    return currentUser;
  }

  function clearSession() {
    isAuthenticated.value = false;
    userName.value = '';
    userEmail.value = '';
    userRoles.value = [];
    userProfile.value = null;
    accessToken.value = '';
    lastAction.value = '';
    lastActionPayload.value = '';
    account.value = null;
    isLoading.value = false;
    userType.value = null;
  }

  return {
    userName,
    userEmail,
    userRoles,
    user,
    userProfile,
    isAuthenticated,
    accessToken,
    lastAction,
    lastActionPayload,
    account,
    isLoading,
    culture,
    userType,
    setUserName,
    setUserEmail,
    setUserRoles,
    setUser,
    setUserProfile,
    setUserPhoto,
    setIsAuthenticated,
    setAccessToken,
    setLastAction,
    setLastActionPayload,
    setAccount,
    setLoading,
    setCulture,
    getUser,
    clearSession,
  };
});
